// ── AI 生成相關 API ──────────────────────────────────────────────────────────

import { apiFetchWithRetry, API_BASE_URL } from './client';
import type { GeneratedResult, GenConfig, OrganizationType } from '../../types';

// ── 生成類 API 共用工具 ────────────────────────────────────────────────────────

/** 使用者身份四欄位（每個生成 API 都會帶這組） */
export interface UserIdentityPayload {
  name: string;
  title?: string | null;
  organizationName?: string | null;
  organizationType: OrganizationType;
}

const appendIdentity = (formData: FormData, p: UserIdentityPayload) => {
  formData.append('name', p.name);
  if (p.title !== undefined && p.title !== null) formData.append('title', p.title);
  if (p.organizationName) formData.append('organizationName', p.organizationName);
  formData.append('organizationType', p.organizationType);
};

interface CampaignParams extends UserIdentityPayload {
  content: string;
  tone: string;
  style: string;
  ratio: string;
  toolkitId?: string;
  assets: File[];
  usePersonalPhoto?: boolean;
  referenceImage?: File;
  additionalRequirements?: string;
}

/** 將生成參數組裝為 FormData（D-2：消除 mode1/mode2 重複邏輯） */
const buildCampaignFormData = (params: CampaignParams): FormData => {
  const formData = new FormData();
  appendIdentity(formData, params);
  formData.append('content', params.content);
  formData.append('tone', params.tone);
  formData.append('style', params.style);
  formData.append('ratio', params.ratio);
  if (params.toolkitId) formData.append('toolkitId', params.toolkitId);
  if (params.usePersonalPhoto) formData.append('usePersonalPhoto', 'true');
  params.assets.forEach(file => formData.append('assets', file));
  if (params.referenceImage) formData.append('referenceImage', params.referenceImage);
  if (params.additionalRequirements) formData.append('additionalRequirements', params.additionalRequirements);
  return formData;
};

// ── Mode 1 生成 ──────────────────────────────────────────────────────────────

export const generateCampaign = async (params: CampaignParams): Promise<GeneratedResult & { historyId: string }> => {
  const res = await apiFetchWithRetry(
    `${API_BASE_URL}/generate/mode1`,
    { method: 'POST', body: buildCampaignFormData(params) },
    120000,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err).error || 'Failed to generate campaign');
  }
  return res.json();
};

// ── Mode 2 生成 ──────────────────────────────────────────────────────────────

export interface Mode2Result {
  imageUrl: string;
  gcsPath: string;
  fileId?: string;
  /** 這次生成所屬的生成流程紀錄 id */
  generationSessionId?: string;
}

export interface Mode2FlowParams extends UserIdentityPayload {
  /** JSON.stringify(Record<string, string | boolean>) — FlowModel answers */
  answers: string;
  /** JSON.stringify(Record<string, string[]>) — pre-uploaded image fileIds per question */
  imageAnswers?: string;
  assets?: File[];
  /** FlowModel DB id. Omit to use the server-side DEFAULT_FLOW_MODEL. */
  flowModelId?: string;
  referenceImageFileId?: string;
  additionalRequirements?: string;
  /** 本段流程既有的生成流程紀錄 id（每次生成覆蓋它） */
  generationSessionId?: string;
  /** 從圖片庫繼續生成時的來源紀錄 id（只在該段流程第一次生成時帶） */
  parentGenerationSessionId?: string;
  /** 這段流程已經開始的 AI 對話 id（還原後先聊再生成時，對話早於紀錄存在） */
  chatSessionId?: string;
}

const buildMode2FormData = (params: Mode2FlowParams): FormData => {
  const formData = new FormData();
  appendIdentity(formData, params);
  formData.append('answers', params.answers);
  if (params.imageAnswers) formData.append('imageAnswers', params.imageAnswers);
  if (params.flowModelId) formData.append('flowModelId', params.flowModelId);
  params.assets?.forEach(file => formData.append('assets', file));
  if (params.referenceImageFileId) formData.append('referenceImageFileId', params.referenceImageFileId);
  if (params.additionalRequirements) formData.append('additionalRequirements', params.additionalRequirements);
  if (params.generationSessionId) formData.append('generationSessionId', params.generationSessionId);
  if (params.parentGenerationSessionId) formData.append('parentGenerationSessionId', params.parentGenerationSessionId);
  if (params.chatSessionId) formData.append('chatSessionId', params.chatSessionId);
  return formData;
};

export const generateMode2 = async (params: Mode2FlowParams): Promise<Mode2Result> => {
  const res = await apiFetchWithRetry(
    `${API_BASE_URL}/generate/mode2`,
    { method: 'POST', body: buildMode2FormData(params) },
    120000,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = (err).error || 'Failed to generate mode 2 image';
    const error = new Error(msg) as any;
    if (res.status === 400) error.isCommunityViolation = true;
    if (res.status === 402) error.isPointsExhausted = true;
    throw error;
  }
  return res.json();
};

export const generateMode2Caption = async (params: {
  imageUrl: string;
  genConfig: GenConfig;
} & UserIdentityPayload): Promise<{ plainText: string; markdown: string }> => {
  const formData = new FormData();
  formData.append('imageUrl', params.imageUrl);
  appendIdentity(formData, params);
  formData.append('answers', JSON.stringify(params.genConfig.answers));
  if (params.genConfig.flowModelId) formData.append('flowModelId', params.genConfig.flowModelId);
  if (params.genConfig.additionalRequirements) formData.append('additionalRequirements', params.genConfig.additionalRequirements);
  params.genConfig.uploadedAssets.forEach(file => formData.append('referenceImages', file));

  const res = await apiFetchWithRetry(
    `${API_BASE_URL}/generate/mode2/caption`,
    { method: 'POST', body: formData },
    120000,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = (err).error || '文案生成失敗';
    const error = new Error(msg) as any;
    if (res.status === 400) error.isCommunityViolation = true;
    if (res.status === 402) error.isPointsExhausted = true;
    throw error;
  }
  return res.json();
};

// ── Style Transfer 生成 ──────────────────────────────────────────────────────

export interface StyleTransferResult { imageUrl: string; gcsPath: string; }

export const generateStyleTransfer = async (imageFile: File, styleId: string, extraRequirements?: string): Promise<StyleTransferResult> => {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('styleId', styleId);
  if (extraRequirements?.trim()) {
    formData.append('extraRequirements', extraRequirements.trim());
  }

  const res = await apiFetchWithRetry(
    `${API_BASE_URL}/generate/style-transfer`,
    { method: 'POST', body: formData },
    120000,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = (err).error || 'Style transfer failed';
    const error = new Error(msg) as any;
    if (res.status === 400) error.isCommunityViolation = true;
    if (res.status === 402) error.isPointsExhausted = true;
    throw error;
  }
  return res.json();
};

// ── Chat APIs ─────────────────────────────────────────────────────────────────

export interface ChatResponse {
  chatId: string;
  responseMessage: string;
  responseImage: string | null;
  userImages: { url: string; gcsPath: string }[];
  textTokenUsage: { inputTokens: number; outputTokens: number; totalTokens: number; };
  imageTokenUsage: { inputTokens: number; outputTokens: number; totalTokens: number; };
  suggestions: string[];
}

export const sendChatMessage = async (
  message: string,
  chatId?: string,
  images?: File[],
  config?: GenConfig & Partial<UserIdentityPayload> & { uploadedAssets?: File[] }
): Promise<ChatResponse> => {
  const formData = new FormData();
  formData.append('message', message);
  if (chatId) formData.append('chatId', chatId);

  images?.forEach(file => formData.append('files', file));

  if (config) {
    formData.append('ratio', (config.answers['imageAspectRatio'] as string) ?? '');
    formData.append('style', (config.answers['imageStyle'] as string) ?? '');
    formData.append('tone', (config.answers['textToneInImage'] as string) ?? '');
    formData.append('content', (config.answers['contentInImage'] as string) ?? '');
    if (config.flowModelId) formData.append('toolkitId', config.flowModelId);
    if (config.name) formData.append('name', config.name);
    if (config.title) formData.append('title', config.title);
    if (config.organizationName) formData.append('organizationName', config.organizationName);
    if (config.organizationType) formData.append('organizationType', config.organizationType);
    config.uploadedAssets?.forEach(file => formData.append('files', file));
    if (config.answers['usePersonalPhoto']) formData.append('usePersonalPhoto', 'true');
    if (config.continuationFeedback) formData.append('continuationFeedback', config.continuationFeedback);
  }

  const res = await apiFetchWithRetry(
    `${API_BASE_URL}/generate/chat`,
    { method: 'POST', body: formData },
    120000,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err).error || 'Chat failed');
  }
  return res.json();
};

// ── AI 小幫手 APIs ────────────────────────────────────────────────────────────

export interface AssistantPageContext {
  pageId: string;
  pageLabel: string;
  globalState: {
    organizationName?: string | null;
    organizationType: OrganizationType;
    name: string;
    title?: string | null;
  };
  quickGen?: { step: number; ratio: string; style: string; tone: string; content: string };
  settings?: {
    organizationName?: string | null;
    organizationType: OrganizationType;
    name: string;
    title?: string | null;
  };
}

export interface AssistantAction {
  type: 'navigate' | 'set-gen-config' | 'set-gen-step' | 'set-global-setting' | 'dispatch-event' | 'hint';
  description: string;
  tab?: string;
  payload?: Record<string, unknown>;
  step?: number;
  eventName?: string;
  detail?: Record<string, unknown>;
  message?: string;
}

export interface AssistantChatApiResponse {
  sessionId: string;
  text: string;
  actions: AssistantAction[];
}

export const sendAssistantMessage = async (params: {
  sessionId?: string;
  message: string;
  pageContext: AssistantPageContext;
}): Promise<AssistantChatApiResponse> => {
  const res = await apiFetchWithRetry(
    `${API_BASE_URL}/generate/assistant/chat`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
    60000,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(((err as Record<string, string>).error) || 'Assistant chat failed');
  }
  return res.json();
};

// ── QuickGen Chat APIs ────────────────────────────────────────────────────────

export type QuickGenChatAction =
  | { action: 'settingChange'; field: string; label: string }
  | { action: 'confirmGenerate'; visualModifications: string }
  | { action: 'askQuestions'; questions: { title: string, options: string[] }[] }
  | { action: 'info' };

export interface QuickGenChatResponse {
  sessionId: string;
  text: string;
  actions: QuickGenChatAction[];
}

export const sendQuickGenChat = async (params: {
  sessionId?: string;
  message: string;
  /** 使用者實際打的字（不含系統註記），僅供還原對話時顯示 */
  displayMessage?: string;
  /** 建立新 session 時必須提供 */
  imageUrl?: string;
  /** MediaFile ID，切換圖片時用來建立新 session */
  imageFileId?: string;
  name?: string;
  title?: string | null;
  organizationName?: string | null;
  organizationType?: OrganizationType;
  flowModelId?: string;
  settings?: Record<string, string | boolean>;
  /** 建立新對話時，要複製其訊息當開頭的來源對話（從圖片庫還原後的分支） */
  parentChatSessionId?: string;
  /** 這段對話所屬的生成流程紀錄，用來把對話連結補寫回紀錄 */
  generationSessionId?: string;
}): Promise<QuickGenChatResponse> => {
  const formData = new FormData();
  formData.append('message', params.message);
  if (params.displayMessage) formData.append('displayMessage', params.displayMessage);
  if (params.sessionId) formData.append('sessionId', params.sessionId);
  if (params.imageUrl) formData.append('imageUrl', params.imageUrl);
  if (params.imageFileId) formData.append('imageFileId', params.imageFileId);
  if (params.name) formData.append('name', params.name);
  if (params.title) formData.append('title', params.title);
  if (params.organizationName) formData.append('organizationName', params.organizationName);
  if (params.organizationType) formData.append('organizationType', params.organizationType);
  if (params.flowModelId) formData.append('flowModelId', params.flowModelId);
  if (params.settings) formData.append('answers', JSON.stringify(params.settings));
  if (params.parentChatSessionId) formData.append('parentChatSessionId', params.parentChatSessionId);
  if (params.generationSessionId) formData.append('generationSessionId', params.generationSessionId);

  const res = await apiFetchWithRetry(
    `${API_BASE_URL}/generate/quick-gen-chat`,
    { method: 'POST', body: formData },
    60000,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error || 'QuickGen chat failed');
  }
  return res.json();
};
